import dayjs from 'dayjs';

/**
 * Convert Firestore timestamp to JavaScript Date
 * @param {Object|string|Date} timestamp - Firestore timestamp object or regular date
 * @returns {Date|null} JavaScript Date object
 */
export const convertFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // If it's already a Date object or ISO string, return as is
  if (timestamp instanceof Date || typeof timestamp === 'string') {
    return timestamp;
  }
  
  // If it's a Firestore timestamp object with _seconds
  if (timestamp && typeof timestamp === 'object' && timestamp._seconds !== undefined) {
    return new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
  }
  
  // If it's a regular timestamp object (e.g., from BigQuery)
  if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
    return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  }
  
  return timestamp;
};

/**
 * Format timestamp using dayjs with Firestore support
 * @param {Object|string|Date} timestamp - Timestamp to format
 * @param {string} format - dayjs format string
 * @returns {string} Formatted date string
 */
export const formatTimestamp = (timestamp, format = 'YYYY-MM-DD HH:mm:ss') => {
  const convertedTimestamp = convertFirestoreTimestamp(timestamp);
  if (!convertedTimestamp) return 'Invalid Date';
  
  try {
    return dayjs(convertedTimestamp).format(format);
  } catch (error) {
    console.warn('Error formatting timestamp:', timestamp, error);
    return 'Invalid Date';
  }
};

/**
 * Calculate duration between two timestamps
 * @param {Object|string|Date} startTime - Start timestamp
 * @param {Object|string|Date} endTime - End timestamp
 * @param {string} unit - Duration unit ('minute', 'second', etc.)
 * @returns {number} Duration in specified unit
 */
export const calculateDuration = (startTime, endTime, unit = 'minute') => {
  const start = convertFirestoreTimestamp(startTime);
  const end = convertFirestoreTimestamp(endTime);
  
  if (!start || !end) return 0;
  
  try {
    return dayjs(end).diff(dayjs(start), unit);
  } catch (error) {
    console.warn('Error calculating duration:', startTime, endTime, error);
    return 0;
  }
};
